import { useCallback, useRef, useState } from 'react';

/**
 * Local editable state that mirrors an external `value` until the caller edits it, after which
 * local edits win over further upstream changes.
 *
 * Resyncs during render rather than in an effect: an effect runs after React paints, so a
 * hydration-time correction would briefly commit the stale value alongside the corrected prop
 * (e.g. "Save Changes" flashing enabled). Assigning during render lets React re-render with the
 * correct value before anything paints -- React's documented pattern for adjusting state to a
 * prop change.
 */
export const useResyncingState = <T>(value: T): [T, (nextValue: T) => void] => {
  const [state, setState] = useState(value);
  const [lastSyncedValue, setLastSyncedValue] = useState(value);
  const hasEditedRef = useRef(false);

  if (!hasEditedRef.current && !Object.is(value, lastSyncedValue)) {
    setLastSyncedValue(value);
    setState(value);
  }

  const setEditedState = useCallback<(nextValue: T) => void>((nextValue) => {
    hasEditedRef.current = true;
    setState(nextValue);
  }, []);

  return [state, setEditedState];
};

export default useResyncingState;
